import React, { createContext, useContext, useEffect, useState } from 'react';
import { ApiConfig, LoggingConfig, AuthConfig } from '../types';

const NONPROD_CLIENT_ID = '2phmss955so8ih4djroqu6j4jt';
const NONPROD_API_URL =
  'https://nonprod.api.oauth.agero.com/multi-nonprod/api/v1';
const NONPROD_WEB_DOMAIN = 'login.nonprod.oauth.agero.com';

const LOCALHOST_REDIRECT_URI = 'http://localhost:8000/callback';
const LOCALHOST_SIGNOUT_URI = 'http://localhost:8000/signout';

const NONPROD_API_BASE_URL = 'http://localhost:5000';

const ACTIVITY_TIMEOUT = 3600;
const API_TIMEOUT = 10000;

export interface ConfigContextProps {
  auth: AuthConfig;
  api: ApiConfig;
  logging: LoggingConfig;
}

export const ConfigContext = createContext<ConfigContextProps | null>(null);

export const ConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = useState<ConfigContextProps | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth: AuthConfig = {
      clientId: process.env.REACT_APP_AUTH_CLIENT_ID || NONPROD_CLIENT_ID,
      apiFullUrl: process.env.REACT_APP_AUTH_API_URL || NONPROD_API_URL,
      appWebDomain:
        process.env.REACT_APP_AUTH_APP_WEB_DOMAIN || NONPROD_WEB_DOMAIN,
      redirectUriSignIn:
        process.env.REACT_APP_AUTH_REDIRECT_URI_SIGN_IN ||
        LOCALHOST_REDIRECT_URI,
      redirectUriSignOut:
        process.env.REACT_APP_AUTH_REDIRECT_URI_SIGN_OUT ||
        LOCALHOST_SIGNOUT_URI,
      useMultiTenantApi: process.env.REACT_APP_USE_MULTI_TENANT_API === 'true',
      activityTimeout: process.env.REACT_APP_ACTIVITY_TIMEOUT
        ? parseInt(process.env.REACT_APP_ACTIVITY_TIMEOUT, 10)
        : ACTIVITY_TIMEOUT,
      refreshTimeoutOffset: process.env.REACT_APP_REFRESH_TIMEOUT_OFFSET
        ? parseInt(process.env.REACT_APP_REFRESH_TIMEOUT_OFFSET, 10)
        : 300,
      basicUser: process.env.REACT_APP_BASIC_USER || 'cv:basic:*',
      tokenScopesArray: ['email', 'profile', 'openid'],
      idpEnvironment: process.env.REACT_APP_IDP_ENVIRONMENT || 'nonprod',
    };

    const api: ApiConfig = {
      baseURL: process.env.REACT_APP_API_BASE_URL || NONPROD_API_BASE_URL,
      timeout: process.env.REACT_APP_API_TIMEOUT
        ? Number(process.env.REACT_APP_API_TIMEOUT)
        : API_TIMEOUT,
      // If you need to send cookies with requests, set this to true
      withCredentials: true,
      websocketURL:
        process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:5000',
    };

    const logging: LoggingConfig = {
      baseURL: process.env.REACT_APP_LOGGING_BASE_URL || '',
      includeConsole: process.env.REACT_APP_LOGGING_INCLUDE_CONSOLE === 'true',
      includeApi: process.env.REACT_APP_LOGGING_INCLUDE_API === 'true',
      level: process.env.REACT_APP_LOGGING_LEVEL || 'info',
      timeout: process.env.REACT_APP_LOGGING_TIMEOUT
        ? Number(process.env.REACT_APP_LOGGING_TIMEOUT)
        : 0,
    };

    setConfig({
      auth,
      api,
      logging,
    });

    setLoading(false);
  }, []);

  if (loading) {
    return null;
  }

  if (config === null) {
    return <div>Cannot load configuration!</div>;
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};

export function useConfig(): ConfigContextProps {
  const config = useContext(ConfigContext);
  return config ? config : assertNever();
}

function assertNever(): never {
  throw new Error('Cannot use useConfig outside of ConfigProvider');
}
